import { ALERT_STATUS_SECTIONS } from './alerts';

import type { ListedAlert } from './alertRow';
import type { AlertStatusSection } from './alerts';

/** Which whole-surface state the Alerts Center is in. */
export type AlertsCenterStatus = 'loading' | 'error' | 'empty' | 'ready';

/** One section's rows and pagination handles, as the center's queries resolve them. */
export interface IAlertsCenterSectionInput {
  rows: readonly ListedAlert[];
  /** The group's total from the counts query, which exceeds the rows wherever the list is paged. */
  count: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

/** One group the center renders, in {@link ALERT_STATUS_SECTIONS} order. */
export interface IAlertsCenterSection extends IAlertsCenterSectionInput {
  section: AlertStatusSection;
}

export interface IAlertsCenterView {
  status: AlertsCenterStatus;
  /** The groups that have rows. Empty for every status but `ready`. */
  sections: IAlertsCenterSection[];
}

interface IBuildAlertsCenterViewInput {
  sections: Record<AlertStatusSection, IAlertsCenterSectionInput>;
  /** Any list read still in flight. */
  isLoading: boolean;
  /**
   * The need-attention read failed. Only that one blocks: the center exists to
   * surface what needs attention, so hiding it behind a cheerful archive is
   * worse than the error block, while a failed pending or history read just
   * drops its group.
   */
  hasBlockingError: boolean;
}

/**
 * Folds the center's three list reads and its counts into the one state the
 * surface renders, so `AlertsCenterBase` stays a switch over `status` rather
 * than a nest of conditions.
 *
 * A section with no rows is dropped rather than rendered empty — a group's
 * header count can be non-zero while its page is still empty, which is the
 * paginated-history case.
 */
export function buildAlertsCenterView({
  sections,
  isLoading,
  hasBlockingError,
}: IBuildAlertsCenterViewInput): IAlertsCenterView {
  if (hasBlockingError) {
    return { status: 'error', sections: [] };
  }

  const populated = ALERT_STATUS_SECTIONS.filter((section) => sections[section].rows.length > 0).map((section) => {
    const input = sections[section];

    return {
      ...input,
      section,
      // The counts query zero-fills when it fails and lags a poll behind a fresh
      // archive, either of which would print a total below the rows on screen.
      // The synthetic unverified-phone alert is uncounted for the same reason.
      count: Math.max(input.count, input.rows.length),
    };
  });

  if (populated.length > 0) {
    return { status: 'ready', sections: populated };
  }

  return { status: isLoading ? 'loading' : 'empty', sections: [] };
}
